/**
 * Market-wide crypto figures (24h volume across exchanges, market cap, rank) for every listed coin, from
 * CoinGecko via GET /api/markets/crypto. The API refreshes them every 10 minutes, so screens that show
 * them reload every 5 while mounted; an empty map means the provider is off or down and the figures hide.
 */
import { useEffect } from 'react';
import type { CryptoMarketData, Feed } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';

const store = createStore<{ data: Record<string, CryptoMarketData> }>({ data: {} });
let loadedAt = 0;
let users = 0;
let timer: number | undefined;

async function load() {
  if (Date.now() - loadedAt < 60_000) return;
  loadedAt = Date.now();
  try {
    const r = await api.get<Feed<Record<string, CryptoMarketData>>>('/api/markets/crypto');
    store.set({ data: r.data });
  } catch {
    loadedAt = 0; // keep the last figures; try again on the next tick
  }
}

/** All coins' figures by symbol, kept fresh while a component using it is mounted. */
export function useCryptoMarkets(): Record<string, CryptoMarketData> {
  useEffect(() => {
    if (users++ === 0) {
      void load();
      timer = window.setInterval(load, 5 * 60_000);
    }
    return () => {
      if (--users === 0) window.clearInterval(timer);
    };
  }, []);
  return useStore(store, (s) => s.data);
}

/** One coin's figures, or null (not crypto, unmapped, or the provider is down). */
export function useCryptoMarket(symbol: string): CryptoMarketData | null {
  return useCryptoMarkets()[symbol] ?? null;
}
